import { Snackbar, Alert, AlertTitle } from '@mui/material';
import { useErrorStore } from '@/stores/errorStore';

/**
 * Global Error Snackbar Component
 *
 * Displays error/success/warning/info messages globally
 * Automatically integrates with errorStore
 *
 * Features:
 * - Auto-hide after 6 seconds
 * - Click away to dismiss
 * - Close button
 * - Different colors for different severity levels
 *
 * @example
 * ```tsx
 * // In App.tsx
 * function App() {
 *   return (
 *     <>
 *       <YourApp />
 *       <ErrorSnackbar />
 *     </>
 *   );
 * }
 * ```
 *
 * @example Using in components
 * ```tsx
 * import { useErrorStore } from '@/stores/errorStore';
 *
 * function MyComponent() {
 *   const { showError, showSuccess } = useErrorStore();
 *
 *   const handleSubmit = async () => {
 *     try {
 *       await api.post('/data');
 *       showSuccess('儲存成功！');
 *     } catch (error) {
 *       showError('儲存失敗，請稍後再試');
 *     }
 *   };
 * }
 * ```
 */
export default function ErrorSnackbar() {
  const { message, severity, isOpen, clearError } = useErrorStore();

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    // Don't close on clickaway for errors (only allow manual close)
    if (reason === 'clickaway' && severity === 'error') {
      return;
    }
    clearError();
  };

  /**
   * Get alert title based on severity
   */
  const getAlertTitle = () => {
    switch (severity) {
      case 'error':
        return '錯誤';
      case 'warning':
        return '警告';
      case 'info':
        return '提示';
      case 'success':
        return '成功';
      default:
        return '';
    }
  };

  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        mt: 8, // Margin from top to avoid overlapping with header
      }}
    >
      <Alert
        onClose={handleClose}
        severity={severity}
        variant="filled"
        elevation={6}
        sx={{
          width: '100%',
          minWidth: 300,
          maxWidth: 600,
        }}
      >
        <AlertTitle>{getAlertTitle()}</AlertTitle>
        {message}
      </Alert>
    </Snackbar>
  );
}
